// rc128 — Estado vivo para las tarjetas de Ajustes. Consultas baratas y
// TOLERANTES: si el usuario no tiene permiso o el endpoint falla, la tarjeta
// simplemente no muestra chip (nunca rompe la portada).
import { useQuery } from "@tanstack/react-query";
import { apiCall } from "@/lib/api";
import { useAuthStore } from "@/lib/auth";
import { formatDate } from "@/lib/utils";

export type Chip = { label: string; tone?: "ok" | "warn" | "bad" | "muted" };

const Q = { staleTime: 60_000, retry: 0, refetchOnWindowFocus: false } as const;

type ConfigItem = { key: string; value: unknown };

export function useAjustesStatus(): Record<string, Chip[]> {
  const empresas = useQuery({
    queryKey: ["ajustes-status", "empresas"],
    queryFn: () => apiCall<{ id: number; activa: boolean; has_production_creds: boolean; has_sandbox_creds: boolean }[]>("get", "/api/v1/empresas"),
    ...Q,
  });
  const toku = useQuery({
    queryKey: ["ajustes-status", "toku"],
    queryFn: () => apiCall<{ enabled: boolean; environment: "sandbox" | "production" }>("get", "/api/v1/integrations/payments/toku/config"),
    ...Q,
  });
  const excepciones = useQuery({
    queryKey: ["toku-exceptions-counts"],
    queryFn: () => apiCall<{ counts_by_status: Record<string, number>; counts_by_severidad: Record<string, number> }>(
      "get", "/api/v1/integrations/payments/exceptions?page=1&page_size=1&status=open",
    ),
    ...Q,
  });
  const licencia = useQuery({
    queryKey: ["ajustes-status", "licencia"],
    queryFn: () => apiCall<{ mode: string }>("get", "/api/v1/licensing/status"),
    ...Q,
  });
  const puedeBackups = useAuthStore((st) => st.hasPermission("backups.read"));
  const backups = useQuery({
    queryKey: ["ajustes-status", "backups"],
    queryFn: () => apiCall<{ total: number; ultimo: { created_at: string } | null }>("get", "/api/v1/sistema/backups"),
    ...Q,
    enabled: puedeBackups,
  });
  const apiKeys = useQuery({
    queryKey: ["ajustes-status", "api-keys"],
    queryFn: () => apiCall<unknown>("get", "/api/v1/api-keys"),
    ...Q,
  });
  const byteolt = useQuery({
    queryKey: ["ajustes-status", "byteolt"],
    queryFn: () => apiCall<{ enabled: boolean }>("get", "/api/v1/integraciones/byteolt"),
    ...Q,
  });
  const byteacs = useQuery({
    queryKey: ["ajustes-status", "byteacs"],
    queryFn: () => apiCall<{ enabled: boolean }>("get", "/api/v1/integraciones/byteacs"),
    ...Q,
  });
  const isp = useQuery({
    queryKey: ["config", "isp"],
    queryFn: () => apiCall<ConfigItem[]>("get", "/api/v1/config", { searchParams: { grupo: "isp" } }),
    ...Q,
  });
  const cobranza = useQuery({
    queryKey: ["config", "cobranza"],
    queryFn: () => apiCall<ConfigItem[]>("get", "/api/v1/config", { searchParams: { grupo: "cobranza" } }),
    ...Q,
  });
  const sistema = useQuery({
    queryKey: ["config", "sistema"],
    queryFn: () => apiCall<ConfigItem[]>("get", "/api/v1/config", { searchParams: { grupo: "sistema" } }),
    ...Q,
  });
  const portal = useQuery({
    queryKey: ["config", "portal"],
    queryFn: () => apiCall<ConfigItem[]>("get", "/api/v1/config", { searchParams: { grupo: "portal" } }),
    ...Q,
  });

  const out: Record<string, Chip[]> = {};

  if (isp.data) {
    const nombre = isp.data.find((i) => i.key === "isp.nombre_comercial")?.value;
    const chips: Chip[] = [];
    if (nombre) chips.push({ label: String(nombre), tone: "muted" });
    const tzv = sistema.data?.find((i) => i.key === "sistema.timezone")?.value;
    if (tzv) chips.push({ label: String(tzv).replace("America/", ""), tone: "muted" });
    if (chips.length) out.isp = chips;
  }

  if (empresas.data) {
    const activas = empresas.data.filter((e) => e.activa);
    const env = toku.data?.environment;
    const sinKey = env
      ? activas.filter((e) => !(env === "production" ? e.has_production_creds : e.has_sandbox_creds)).length
      : 0;
    out.empresas = [{ label: `${activas.length} activa${activas.length === 1 ? "" : "s"}`, tone: "muted" }];
    if (sinKey > 0) out.empresas.push({ label: `${sinKey} sin Account Key`, tone: "bad" });
  }

  if (toku.data) {
    out.pagos = toku.data.enabled
      ? [{ label: `Toku · ${toku.data.environment === "production" ? "Producción" : "Sandbox"}`, tone: "ok" }]
      : [{ label: "Toku apagado", tone: "muted" }];
    const open = excepciones.data?.counts_by_status?.open ?? 0;
    const crit = excepciones.data?.counts_by_severidad?.critical ?? 0;
    if (open > 0) out.pagos.push({ label: `${open} excepci${open === 1 ? "ón" : "ones"}`, tone: crit > 0 ? "bad" : "warn" });
  }

  if (cobranza.data) {
    const gracia = cobranza.data.find((i) => i.key === "cobranza.dias_gracia")?.value;
    const hora = cobranza.data.find((i) => i.key === "cobranza.hora_corte")?.value;
    const tope = Number(cobranza.data.find((i) => i.key === "cobranza.promesa_max_por_anio")?.value ?? 0);
    const chips: Chip[] = [];
    if (hora) chips.push({ label: `Corte ${hora}`, tone: "muted" });
    if (gracia !== undefined) chips.push({ label: `${gracia} d gracia`, tone: "muted" });
    chips.push({ label: tope === 0 ? "Promesas sin tope" : `Promesas máx ${tope}/año`, tone: "muted" });
    out.facturacion = chips;
  }

  if (portal.data) {
    const min = Number(portal.data.find((i) => i.key === "portal.session_lifetime_minutes")?.value ?? 480);
    const tel = String(portal.data.find((i) => i.key === "portal.contacto_telefono")?.value ?? "").trim();
    const chips: Chip[] = [{ label: `Sesión ${min % 60 === 0 ? `${min / 60} h` : `${min} min`}`, tone: "muted" }];
    chips.push(tel ? { label: "Contacto configurado", tone: "ok" } : { label: "Sin teléfono de contacto", tone: "warn" });
    out.portal = chips;
  }

  if (licencia.data) {
    const m = licencia.data.mode;
    const map: Record<string, Chip> = {
      active: { label: "Activa", tone: "ok" },
      grace: { label: "En gracia", tone: "warn" },
      blocked: { label: "Bloqueada", tone: "bad" },
      no_license: { label: "Sin licencia", tone: "warn" },
      off: { label: "Apagada", tone: "muted" },
    };
    out.licencia = [map[m] ?? { label: m, tone: "muted" }];
  }

  if (backups.data) {
    out.backups = backups.data.ultimo
      ? [{ label: `Último ${formatDate(backups.data.ultimo.created_at)}`, tone: "ok" }, { label: `${backups.data.total} guardados`, tone: "muted" }]
      : [{ label: "Sin respaldos", tone: "bad" }];
  }

  if (apiKeys.data) {
    const d = apiKeys.data as { items?: unknown[] } | unknown[];
    const n = Array.isArray(d) ? d.length : Array.isArray(d.items) ? d.items.length : null;
    if (n !== null) out["api-keys"] = [{ label: `${n} llave${n === 1 ? "" : "s"}`, tone: "muted" }];
  }

  if (byteolt.data || byteacs.data) {
    const chips: Chip[] = [];
    if (byteolt.data) chips.push({ label: byteolt.data.enabled ? "OLT ✓" : "OLT —", tone: byteolt.data.enabled ? "ok" : "muted" });
    if (byteacs.data) chips.push({ label: byteacs.data.enabled ? "ACS ✓" : "ACS —", tone: byteacs.data.enabled ? "ok" : "muted" });
    out.integraciones = chips;
  }

  return out;
}
